// Barra de filtros do painel (§6.1): busca por white label,
// processor, status do certificado e "só cert expirando".
// Componente CONTROLADO — o estado mora no painel (fonte única).
import { SearchIcon, XIcon } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import type {
  ProcessorCertificateStatus,
  ProcessorGateway,
} from "@/types/admin-processor-config"

import {
  EMPTY_PANEL_FILTERS,
  type PanelFilters,
} from "../utils"

const panelPrefix = "pages.adminProcessorConfig"

// Sentinela de "todos" nos Selects (Radix não aceita value "").
const ALL_VALUE = "ALL"

const CERTIFICATE_STATUSES: ProcessorCertificateStatus[] = [
  "VALID",
  "EXPIRING",
  "EXPIRED",
  "NOT_DEFINED",
  "NOT_APPLICABLE",
]

type ProcessorConfigFiltersProps = {
  filters: PanelFilters
  // Gateways conhecidos (metadata quando disponível).
  gateways: Array<{
    gateway: ProcessorGateway
    label: string
  }>
  onChange: (filters: PanelFilters) => void
}

export const ProcessorConfigFilters = ({
  filters,
  gateways,
  onChange,
}: ProcessorConfigFiltersProps) => {
  const { t } = useTranslation()

  const hasActiveFilters =
    filters.search !== "" ||
    filters.gateway !== null ||
    filters.certificateStatus !== null ||
    filters.expiringOnly

  return (
    <div className="flex flex-col gap-3 @2xl:flex-row @2xl:items-center">
      <div className="relative flex-1 @2xl:max-w-xs">
        <SearchIcon
          className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          className="pl-8"
          placeholder={t(
            `${panelPrefix}.filters.searchPlaceholder`
          )}
          aria-label={t(
            `${panelPrefix}.filters.searchPlaceholder`
          )}
          value={filters.search}
          onChange={(event) =>
            onChange({
              ...filters,
              search: event.target.value,
            })
          }
        />
      </div>

      <Select
        value={filters.gateway ?? ALL_VALUE}
        onValueChange={(value) =>
          onChange({
            ...filters,
            gateway:
              value === ALL_VALUE
                ? null
                : (value as ProcessorGateway),
          })
        }
      >
        <SelectTrigger
          className="w-full @2xl:w-48"
          aria-label={t(
            `${panelPrefix}.filters.gatewayLabel`
          )}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_VALUE}>
            {t(`${panelPrefix}.filters.allGateways`)}
          </SelectItem>
          {gateways.map(({ gateway, label }) => (
            <SelectItem key={gateway} value={gateway}>
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={filters.certificateStatus ?? ALL_VALUE}
        onValueChange={(value) =>
          onChange({
            ...filters,
            certificateStatus:
              value === ALL_VALUE
                ? null
                : (value as ProcessorCertificateStatus),
          })
        }
      >
        <SelectTrigger
          className="w-full @2xl:w-52"
          aria-label={t(
            `${panelPrefix}.filters.certificateStatusLabel`
          )}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_VALUE}>
            {t(
              `${panelPrefix}.filters.allCertificateStatuses`
            )}
          </SelectItem>
          {CERTIFICATE_STATUSES.map((status) => (
            <SelectItem key={status} value={status}>
              {t(
                `${panelPrefix}.certificate.status.${status}`
              )}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="flex items-center gap-2">
        <Checkbox
          id="processor-config-expiring-only"
          checked={filters.expiringOnly}
          onCheckedChange={(checked) =>
            onChange({
              ...filters,
              expiringOnly: checked === true,
            })
          }
        />
        <Label
          htmlFor="processor-config-expiring-only"
          className="cursor-pointer text-sm font-normal"
        >
          {t(`${panelPrefix}.filters.expiringOnly`)}
        </Label>
      </div>

      {hasActiveFilters && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="w-fit text-muted-foreground"
          onClick={() =>
            onChange({ ...EMPTY_PANEL_FILTERS })
          }
        >
          <XIcon
            className="mr-1 size-3.5"
            aria-hidden="true"
          />
          {t(`${panelPrefix}.filters.clear`)}
        </Button>
      )}
    </div>
  )
}
